import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../context/TenantContext';

const TYPE_META = {
  sql:         { label: 'SQL-Datenbank',       icon: '🗄️' },
  rest:        { label: 'REST-API',            icon: '🌐' },
  sharepoint:  { label: 'SharePoint',          icon: '📂' },
  webhook:     { label: 'Webhook',             icon: '🔔' }
};

const EMPTY_BY_TYPE = {
  sql:         { driver: 'postgres', host: '', port: '', database: '', username: '', password: '', ssl: false },
  rest:        { url: '', testMethod: 'GET', apiKeyHeader: 'X-API-Key', apiKey: '', bearerToken: '', username: '', password: '' },
  sharepoint:  { url: '', tenantId: '', clientId: '', clientSecret: '' },
  webhook:     { url: '', secret: '', apiKeyHeader: 'X-API-Key', apiKey: '' }
};

export default function DataSourcesPanel({ canEdit }) {
  const { activeTenant, tenantFetch } = useTenant();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { mode, type, _id?, name, description, config }
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!activeTenant) return;
    setLoading(true);
    try {
      const res = await tenantFetch('/api/data-sources');
      const data = await res.json();
      if (res.ok) setSources(data.dataSources || []);
    } finally {
      setLoading(false);
    }
  }, [activeTenant, tenantFetch]);

  useEffect(() => { load(); }, [load]);

  const startCreate = (type) => {
    setEditing({
      mode: 'create',
      type,
      name: '',
      description: '',
      config: { ...EMPTY_BY_TYPE[type] }
    });
    setTestResult(null);
    setError('');
  };

  const startEdit = (ds) => {
    setEditing({
      mode: 'edit',
      type: ds.type,
      _id: ds._id,
      name: ds.name,
      description: ds.description || '',
      config: { ...EMPTY_BY_TYPE[ds.type], ...(ds.config || {}) }
    });
    setTestResult(null);
    setError('');
  };

  const closeEditor = () => {
    setEditing(null);
    setTestResult(null);
    setError('');
  };

  const setCfg = (key, value) => {
    setEditing(prev => prev ? { ...prev, config: { ...prev.config, [key]: value } } : prev);
  };

  const handleSave = async () => {
    if (!editing) return;
    setBusy(true);
    setError('');
    try {
      const body = { name: editing.name, description: editing.description, config: editing.config };
      let res;
      if (editing.mode === 'create') {
        res = await tenantFetch('/api/data-sources', {
          method: 'POST',
          body: JSON.stringify({ type: editing.type, ...body })
        });
      } else {
        res = await tenantFetch(`/api/data-sources/${editing._id}`, {
          method: 'PATCH',
          body: JSON.stringify(body)
        });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler beim Speichern');
      await load();
      closeEditor();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    if (!editing) return;
    setBusy(true);
    setTestResult(null);
    try {
      let res;
      if (editing.mode === 'edit' && editing._id) {
        res = await tenantFetch(`/api/data-sources/${editing._id}/test`, { method: 'POST' });
      } else {
        res = await tenantFetch('/api/data-sources/test', {
          method: 'POST',
          body: JSON.stringify({ type: editing.type, config: editing.config })
        });
      }
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (ds) => {
    if (!window.confirm(`Datenquelle "${ds.name}" wirklich löschen?`)) return;
    try {
      const res = await tenantFetch(`/api/data-sources/${ds._id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler beim Löschen');
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="data-sources-panel">
      {loading ? (
        <p>Lädt…</p>
      ) : sources.length === 0 ? (
        <p className="data-sources-empty">Noch keine Datenquellen konfiguriert.</p>
      ) : (
        <ul className="data-sources-list">
          {sources.map(ds => (
            <li key={ds._id} className="data-source-item">
              <div className="data-source-icon">{TYPE_META[ds.type]?.icon || '📦'}</div>
              <div className="data-source-info">
                <div className="data-source-name">{ds.name}</div>
                <div className="data-source-meta">
                  <span className="data-source-type-tag">{TYPE_META[ds.type]?.label || ds.type}</span>
                  {ds.description && <span className="data-source-desc">{ds.description}</span>}
                </div>
              </div>
              {canEdit && (
                <div className="data-source-actions">
                  <button className="link-btn" onClick={() => startEdit(ds)}>Bearbeiten</button>
                  <button className="link-btn danger" onClick={() => handleDelete(ds)}>Löschen</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="data-source-create-row">
          <span className="data-source-create-label">Neue Quelle:</span>
          {Object.keys(TYPE_META).map(t => (
            <button key={t} className="data-source-type-btn" onClick={() => startCreate(t)}>
              <span>{TYPE_META[t].icon}</span> {TYPE_META[t].label}
            </button>
          ))}
        </div>
      )}

      {editing && (
        <div className="data-source-editor-overlay" onClick={closeEditor}>
          <div className="data-source-editor" onClick={e => e.stopPropagation()}>
            <div className="data-source-editor-header">
              <h3>
                {TYPE_META[editing.type]?.icon} {editing.mode === 'create' ? 'Neue' : 'Bearbeiten'} — {TYPE_META[editing.type]?.label}
              </h3>
              <button className="icon-btn" onClick={closeEditor}>✕</button>
            </div>
            <div className="data-source-editor-body">
              <label className="data-source-field">
                <span>Name *</span>
                <input
                  className="text-input"
                  value={editing.name}
                  onChange={e => setEditing({ ...editing, name: e.target.value })}
                />
              </label>
              <label className="data-source-field">
                <span>Beschreibung</span>
                <input
                  className="text-input"
                  value={editing.description}
                  onChange={e => setEditing({ ...editing, description: e.target.value })}
                />
              </label>

              <div className="data-source-config">
                {editing.type === 'sql' && (
                  <>
                    <label className="data-source-field">
                      <span>Treiber</span>
                      <select value={editing.config.driver} onChange={e => setCfg('driver', e.target.value)}>
                        <option value="postgres">PostgreSQL</option>
                        <option value="mysql">MySQL / MariaDB</option>
                        <option value="mssql">Microsoft SQL Server</option>
                      </select>
                    </label>
                    <div className="data-source-row">
                      <label className="data-source-field">
                        <span>Host *</span>
                        <input className="text-input" value={editing.config.host} onChange={e => setCfg('host', e.target.value)} />
                      </label>
                      <label className="data-source-field small">
                        <span>Port</span>
                        <input className="text-input" value={editing.config.port} onChange={e => setCfg('port', e.target.value)} />
                      </label>
                    </div>
                    <label className="data-source-field">
                      <span>Datenbank *</span>
                      <input className="text-input" value={editing.config.database} onChange={e => setCfg('database', e.target.value)} />
                    </label>
                    <div className="data-source-row">
                      <label className="data-source-field">
                        <span>Benutzer</span>
                        <input className="text-input" value={editing.config.username} onChange={e => setCfg('username', e.target.value)} />
                      </label>
                      <label className="data-source-field">
                        <span>Passwort</span>
                        <input type="password" className="text-input" value={editing.config.password} onChange={e => setCfg('password', e.target.value)} placeholder={editing.mode === 'edit' ? 'Unverändert' : ''} />
                      </label>
                    </div>
                    <label className="data-source-field inline">
                      <input type="checkbox" checked={!!editing.config.ssl} onChange={e => setCfg('ssl', e.target.checked)} />
                      <span>SSL/TLS verwenden</span>
                    </label>
                  </>
                )}

                {editing.type === 'rest' && (
                  <>
                    <label className="data-source-field">
                      <span>Basis-URL *</span>
                      <input className="text-input" value={editing.config.url} onChange={e => setCfg('url', e.target.value)} placeholder="https://api.example.com" />
                    </label>
                    <label className="data-source-field">
                      <span>Test-Methode</span>
                      <select value={editing.config.testMethod} onChange={e => setCfg('testMethod', e.target.value)}>
                        <option value="GET">GET</option>
                        <option value="HEAD">HEAD</option>
                      </select>
                    </label>
                    <div className="data-source-row">
                      <label className="data-source-field">
                        <span>API-Key Header</span>
                        <input className="text-input" value={editing.config.apiKeyHeader} onChange={e => setCfg('apiKeyHeader', e.target.value)} />
                      </label>
                      <label className="data-source-field">
                        <span>API-Key</span>
                        <input type="password" className="text-input" value={editing.config.apiKey} onChange={e => setCfg('apiKey', e.target.value)} placeholder={editing.mode === 'edit' ? 'Unverändert' : ''} />
                      </label>
                    </div>
                    <label className="data-source-field">
                      <span>Bearer-Token (alternativ)</span>
                      <input type="password" className="text-input" value={editing.config.bearerToken} onChange={e => setCfg('bearerToken', e.target.value)} placeholder={editing.mode === 'edit' ? 'Unverändert' : ''} />
                    </label>
                    <div className="data-source-row">
                      <label className="data-source-field">
                        <span>Basic-Auth Benutzer</span>
                        <input className="text-input" value={editing.config.username} onChange={e => setCfg('username', e.target.value)} />
                      </label>
                      <label className="data-source-field">
                        <span>Basic-Auth Passwort</span>
                        <input type="password" className="text-input" value={editing.config.password} onChange={e => setCfg('password', e.target.value)} placeholder={editing.mode === 'edit' ? 'Unverändert' : ''} />
                      </label>
                    </div>
                  </>
                )}

                {editing.type === 'sharepoint' && (
                  <>
                    <label className="data-source-field">
                      <span>Site-URL *</span>
                      <input className="text-input" value={editing.config.url} onChange={e => setCfg('url', e.target.value)} placeholder="https://{tenant}.sharepoint.com/sites/..." />
                    </label>
                    <label className="data-source-field">
                      <span>Azure Tenant-ID</span>
                      <input className="text-input" value={editing.config.tenantId} onChange={e => setCfg('tenantId', e.target.value)} />
                    </label>
                    <div className="data-source-row">
                      <label className="data-source-field">
                        <span>Client-ID</span>
                        <input className="text-input" value={editing.config.clientId} onChange={e => setCfg('clientId', e.target.value)} />
                      </label>
                      <label className="data-source-field">
                        <span>Client-Secret</span>
                        <input type="password" className="text-input" value={editing.config.clientSecret} onChange={e => setCfg('clientSecret', e.target.value)} placeholder={editing.mode === 'edit' ? 'Unverändert' : ''} />
                      </label>
                    </div>
                  </>
                )}

                {editing.type === 'webhook' && (
                  <>
                    <label className="data-source-field">
                      <span>Webhook-URL *</span>
                      <input className="text-input" value={editing.config.url} onChange={e => setCfg('url', e.target.value)} placeholder="https://hooks.example.com/..." />
                    </label>
                    <div className="data-source-row">
                      <label className="data-source-field">
                        <span>API-Key Header</span>
                        <input className="text-input" value={editing.config.apiKeyHeader} onChange={e => setCfg('apiKeyHeader', e.target.value)} />
                      </label>
                      <label className="data-source-field">
                        <span>API-Key</span>
                        <input type="password" className="text-input" value={editing.config.apiKey} onChange={e => setCfg('apiKey', e.target.value)} placeholder={editing.mode === 'edit' ? 'Unverändert' : ''} />
                      </label>
                    </div>
                    <label className="data-source-field">
                      <span>Shared Secret (HMAC)</span>
                      <input type="password" className="text-input" value={editing.config.secret} onChange={e => setCfg('secret', e.target.value)} placeholder={editing.mode === 'edit' ? 'Unverändert' : ''} />
                    </label>
                  </>
                )}
              </div>

              {testResult && (
                <div className={`data-source-test-result ${testResult.ok ? 'ok' : 'fail'}`}>
                  {testResult.ok ? '✓ ' : '✗ '}
                  {testResult.message || testResult.error}
                  {testResult.ms != null && <span className="data-source-test-ms"> · {testResult.ms} ms</span>}
                </div>
              )}
              {error && <div className="form-error">{error}</div>}
            </div>
            <div className="data-source-editor-footer">
              <button className="link-btn" onClick={handleTest} disabled={busy}>Verbindung testen</button>
              <div className="data-source-editor-footer-right">
                <button className="link-btn" onClick={closeEditor} disabled={busy}>Abbrechen</button>
                <button className="primary-btn" onClick={handleSave} disabled={busy || !editing.name.trim()}>
                  {busy ? '…' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
